import hoistNonReactStatics from 'hoist-non-react-statics';
import { useDrop, useDrag, useDragLayer, DndProvider } from 'react-dnd';
import { forwardRef, useMemo, useRef } from 'react';
import * as React from 'react';
import { memoizeOne } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
const FALLBACK_TYPE = 'DO_NOT_USE_react-dnd-compat-default';
function toDragSourceMonitor(monitor) {
  return Object.assign({}, monitor, {
    isDragging() {
      return monitor.isDragging();
    },
    canDrag() {
      return monitor.canDrag();
    },
    getItemType() {
      return monitor.getItemType();
    },
    getItem() {
      return monitor.getItem();
    },
    getDropResult() {
      return monitor.getDropResult();
    },
    didDrop() {
      return monitor.didDrop();
    },
    getInitialClientOffset() {
      return monitor.getInitialClientOffset();
    },
    getInitialSourceClientOffset() {
      return monitor.getInitialSourceClientOffset();
    },
    getClientOffset() {
      return monitor.getClientOffset();
    },
    getDifferenceFromInitialOffset() {
      return monitor.getDifferenceFromInitialOffset();
    },
    getSourceClientOffset() {
      return monitor.getSourceClientOffset();
    }
  });
}
export function DragSource(type, spec, mapMonitorToProps) {
  return function (DecoratedComponent) {
    const DragSourceComponent = /*#__PURE__*/forwardRef((props, ref) => {
      const memoizedToDragSourceMonitor = useMemo(() => memoizeOne(toDragSourceMonitor), []);
      const internalRef = useRef(null);
      const resolvedRef = ref || internalRef;
      const compatSpec = {};
      const {
        canDrag,
        isDragging,
        beginDrag,
        endDrag
      } = spec;
      if (beginDrag) {
        compatSpec.item = monitor => {
          return beginDrag(props, memoizedToDragSourceMonitor(monitor), resolvedRef.current);
        };
      }
      if (canDrag) {
        compatSpec.canDrag = monitor => {
          return canDrag(props, memoizedToDragSourceMonitor(monitor));
        };
      }
      if (isDragging) {
        compatSpec.isDragging = monitor => {
          return isDragging(props, memoizedToDragSourceMonitor(monitor));
        };
      }
      if (endDrag) {
        compatSpec.end = (_, monitor) => {
          endDrag(props, memoizedToDragSourceMonitor(monitor), resolvedRef.current);
        };
      }
      const actualType = typeof type === 'function' ? type(props) : type;
      const [collectedProps, connectDragSource, connectDragPreview] = useDrag(Object.assign({
        // this MUST be defined in react-dnd@5 or there will be runtime errors.
        type: actualType || FALLBACK_TYPE,
        collect(monitor) {
          return mapMonitorToProps(memoizedToDragSourceMonitor(monitor));
        }
      }, compatSpec));
      return /*#__PURE__*/_jsx(DecoratedComponent, Object.assign({
        ref: resolvedRef
      }, props, collectedProps, {
        connectDragSource: connectDragSource,
        connectDragPreview: connectDragPreview
      }));
    });
    DragSourceComponent.DecoratedComponent = DecoratedComponent;
    const displayName = DecoratedComponent.displayName || DecoratedComponent.name || 'Component';
    DragSourceComponent.displayName = `DragSource(${displayName})`;
    hoistNonReactStatics(DragSourceComponent, DecoratedComponent);
    return DragSourceComponent;
  };
}
function toDropTargetMonitor(monitor) {
  return Object.assign({}, monitor, {
    isOver(options) {
      return monitor.isOver(options);
    },
    getInitialClientOffset() {
      return monitor.getInitialClientOffset();
    },
    getClientOffset() {
      return monitor.getClientOffset();
    },
    canDrop() {
      return monitor.canDrop();
    },
    getItemType() {
      return monitor.getItemType();
    },
    getItem() {
      return monitor.getItem();
    },
    getDropResult() {
      return monitor.getDropResult();
    },
    didDrop() {
      return monitor.didDrop();
    },
    getInitialSourceClientOffset() {
      return monitor.getInitialSourceClientOffset();
    },
    getDifferenceFromInitialOffset() {
      return monitor.getDifferenceFromInitialOffset();
    },
    getSourceClientOffset() {
      return monitor.getSourceClientOffset();
    }
  });
}
export function DropTarget(type, spec, mapMonitorToProps) {
  return function (DecoratedComponent) {
    const DropTargetComponent = /*#__PURE__*/forwardRef((props, ref) => {
      // Create internal ref if none provided
      const internalRef = useRef(null);
      const resolvedRef = ref || internalRef;
      const memoizedToDropTargetMonitor = useMemo(() => memoizeOne(toDropTargetMonitor), []);
      const compatSpec = {};
      const {
        hover,
        canDrop,
        drop
      } = spec;
      if (hover) {
        compatSpec.hover = (_, monitor) => {
          return hover(props, memoizedToDropTargetMonitor(monitor), resolvedRef.current);
        };
      }
      if (canDrop) {
        compatSpec.canDrop = (_, monitor) => {
          return canDrop(props, memoizedToDropTargetMonitor(monitor));
        };
      }
      if (drop) {
        compatSpec.drop = (_, monitor) => {
          return drop(props, memoizedToDropTargetMonitor(monitor), resolvedRef.current);
        };
      }
      const actualType = typeof type === 'function' ? type(props) : type;
      const [collectedProps, connectDropTarget] = useDrop(Object.assign({
        accept: actualType || FALLBACK_TYPE,
        collect(monitor) {
          return mapMonitorToProps(memoizedToDropTargetMonitor(monitor));
        }
      }, compatSpec));
      return /*#__PURE__*/_jsx(DecoratedComponent, Object.assign({
        ref: resolvedRef
      }, props, collectedProps, {
        connectDropTarget: connectDropTarget
      }));
    });
    DropTargetComponent.DecoratedComponent = DecoratedComponent;
    const displayName = DecoratedComponent.displayName || DecoratedComponent.name || 'Component';
    DropTargetComponent.displayName = `DropTarget(${displayName})`;
    hoistNonReactStatics(DropTargetComponent, DecoratedComponent);
    return DropTargetComponent;
  };
}
function toDragLayerMonitor(monitor) {
  return Object.assign({}, monitor, {
    isDragging() {
      return monitor.isDragging();
    },
    getItemType() {
      return monitor.getItemType();
    },
    getItem() {
      return monitor.getItem();
    },
    getInitialClientOffset() {
      return monitor.getInitialClientOffset();
    },
    getClientOffset() {
      return monitor.getClientOffset();
    },
    getDifferenceFromInitialOffset() {
      return monitor.getDifferenceFromInitialOffset();
    },
    getInitialSourceClientOffset() {
      return monitor.getInitialSourceClientOffset();
    },
    getSourceClientOffset() {
      return monitor.getSourceClientOffset();
    }
  });
}
export function DragLayer(mapMonitorToProps) {
  return function (DecoratedComponent) {
    function DragLayerContainer(props) {
      const memoizedToDragLayerMonitor = useMemo(() => memoizeOne(toDragLayerMonitor), []);
      const collectedProps = useDragLayer(monitor => {
        return mapMonitorToProps(memoizedToDragLayerMonitor(monitor));
      });
      return /*#__PURE__*/_jsx(DecoratedComponent, Object.assign({}, props, collectedProps));
    }
    DragLayerContainer.DecoratedComponent = DecoratedComponent;
    const displayName = DecoratedComponent.displayName || DecoratedComponent.name || 'Component';
    DragLayerContainer.displayName = `DragLayer(${displayName})`;
    hoistNonReactStatics(DragLayerContainer, DecoratedComponent);
    return DragLayerContainer;
  };
}
function makeCreateBackend(backendFactory) {
  return function createBackend(manager, globalContext, ...args) {
    if (!Object.getPrototypeOf(manager).getContext) {
      Object.getPrototypeOf(manager).getContext = () => globalContext;
    }
    return backendFactory(manager, globalContext, ...args);
  };
}
export function DragDropContext(backendFactory,
// In `react-dnd@5` only, we accept this options prop so we can get the BE in tests.
// do not use this anywhere else! It does not exist in `react-dnd@4`.
options) {
  return function (DecoratedComponent) {
    function DragDropContextContainer(props) {
      const createBackend = makeCreateBackend(backendFactory);
      return (
        /*#__PURE__*/
        // @ts-expect-error minor react-dnd@5 type incompat
        _jsx(DndProvider, {
          backend: createBackend,
          options: options,
          children: /*#__PURE__*/_jsx(DecoratedComponent, Object.assign({}, props))
        })
      );
    }
    DragDropContextContainer.DecoratedComponent = DecoratedComponent;
    const displayName = DecoratedComponent.displayName || DecoratedComponent.name || 'Component';
    DragDropContextContainer.displayName = `DragDropContext(${displayName})`;
    return DragDropContextContainer;
  };
}